import React, { useEffect, useMemo } from 'react'
import { TourStop, Skeleton } from 'Components/tourStop'
import { useDispatch, useSelector } from 'react-redux'
import {
  useGetStopsQuery,
  useLazyGetPersonalizedStopsQuery,
  useGetPointsQuery,
  useGetTourStopLabelsQuery,
} from 'Services/institution'
import { useGetAnalyticsQuery } from 'Services/analytics'
import { setLocation } from 'Slices/LocationSlice'

type Point = {
  stopid: number | null
  [key: string]: any
}

export const TourStopsContainer = () => {
  const dispatch = useDispatch()
  const locationState = useSelector((state: any) => state.location)
  const cifStatus = useSelector((state: any) => state.cif)
  const { locId, instId, tourId, instName } = locationState

  const { data: analyticsData } = useGetAnalyticsQuery(
    {
      instId,
      locId,
      tourId,
    },
    { skip: !tourId }
  )

  const userkey = analyticsData?.analytics?.userkey
  const personalization = analyticsData?.personalization_module_enabled

  const [
    fetchPersonalizedStops,
    { data: personalizeStopsData, isLoading: personalizeLoading },
  ] = useLazyGetPersonalizedStopsQuery()

  const { data: tourStopsData, isLoading: stopsLoading } = useGetStopsQuery({
    instId,
    locId,
  })

  const { data: stopLabelsData } = useGetTourStopLabelsQuery({
    instId,
    locId,
  })

  const { data: { resources: pointsData = [] } = {} } = useGetPointsQuery(
    {
      instId,
      locId,
      tourId,
    },
    { skip: !locId || !instId || !tourId }
  )

  const stops =
    Object.values(tourStopsData?.resources || {}).length > 1
      ? (
          Object.values(tourStopsData?.resources || {}).find(
            (tour: any) => tour.tour_name === instName
          ) as { stops: Array<any> } | undefined
        )?.stops
      : (
          Object.values(tourStopsData?.resources || [])[0] as {
            stops: Array<any>
          }
        )?.stops

  const personalizedStops = personalizeStopsData?.resources?.stop_ids?.map(
    (personalizedStop) => {
      const matchingStop = stops?.find(
        (stop) => stop.stopid === personalizedStop.stopid
      )
      return matchingStop
        ? {
            ...personalizedStop,
            main_media_id: matchingStop.main_media_id,
            main_media_type: matchingStop.main_media_type,
          }
        : personalizedStop
    }
  )

  const pointsToStops = (points: Point[], stops: any[]) => {
    if (!points || !stops) return []
    let matchedStopsList = []
    for (const point of points) {
      if (point.stopid) {
        const matchedStop = stops?.find((stop) => stop.stopid === point.stopid)
        matchedStopsList.push({ ...matchedStop, point_id: point.id, media_id: point.media_id })
      }
    }
    return matchedStopsList
  }
  // stops list order is sorted by associated point id
  const orderedStopsList = useMemo(() => pointsToStops(pointsData?.data, stops), [pointsData?.data, stops, stopLabelsData])

  useEffect(() => {
    if (personalization) {
      fetchPersonalizedStops({ instId, locId, userkey, tourId })
    }
  }, [
    personalization,
    fetchPersonalizedStops,
    instId,
    locId,
    userkey,
    tourId,
    cifStatus,
  ])

  useEffect(() => {
    if (orderedStopsList?.length > 0) {
      const introStop = orderedStopsList.find((stop) => stop.intro_media)
      if (introStop) {
        dispatch(
          setLocation({
            ...locationState,
            stops: orderedStopsList,
            introMedia: introStop?.intro_media,
            labels: stopLabelsData,
          })
        )
      } else {
        dispatch(
          setLocation({
            ...locationState,
            stops: orderedStopsList,
            labels: stopLabelsData,
          })
        )
      }
    }
  }, [orderedStopsList, dispatch])

  if (personalizeLoading || stopsLoading) {
    return <Skeleton />
  } else {
    return (
      <>
        {personalizedStops?.map((stop) => {
          return (
            <TourStop personalized={true} key={stop.stopid} stopData={stop} labels={stopLabelsData} />
          )
        })}
        {orderedStopsList?.map((stop) => {
          return <TourStop key={stop.stopid} stopData={stop} labels={stopLabelsData} />
        })}
      </>
    )
  }
}
